'use client';

import { useCartOrderDraft } from '@eliadmin/core/src/hooks/cart/use-cart-order-draft';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import { TrackComponent } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { CartEmpty } from '@eliadmin/core/src/components/cart/cart-empty';
import { CartDetails } from '@eliadmin/core/src/components/cart/detail';
import { CartDistanceShipmentSync } from '@eliadmin/core/src/components/cart/detail/cart-distance-shipment-sync';
import { StripeProvider } from '@eliadmin/core/src/components/cart/detail/stripe/stripe-provider';
import { CartOrder } from '@eliadmin/core/src/components/cart/order';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { ResponsiveDialogProvider } from '@eliadmin/core/src/components/ui/responsive-dialog';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { CartStep, useCartStepper } from '@eliadmin/core/src/hooks/cart/use-cart-stepper';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useSuspenseQueries } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { ZodIssue } from 'zod';
import { ZodError, type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, number as __eliadmin_zod_2, boolean as __eliadmin_zod_3, any as __eliadmin_zod_4 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import { CartOrderShipment } from '../../components/cart/order-shipment';
import CartSummary from '../../components/cart/summary';

const STEP_TO_COMPONENT = {
  [CartStep.DETAIL]: CartDetails,
  [CartStep.PAYMENT_SHIPMENT]: CartOrderShipment,
  [CartStep.ORDER]: CartOrder,
  [CartStep.SUMMARY]: CartSummary,
};

export const createOrderSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
      name: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: __eliadmin_zod_1().min(12, VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE()),
      address: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      street_number: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      city: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      postcode: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      country: __eliadmin_zod_0({ id: __eliadmin_zod_1().min(1).or(__eliadmin_zod_2()) }),

      company_buy: __eliadmin_zod_3().default(false),
      // if company_buy is true
      company: __eliadmin_zod_1().optional(),
      ico: __eliadmin_zod_1().optional(),
      dic: __eliadmin_zod_1().optional(),
      ic_dph: __eliadmin_zod_1().optional(),

      different_delivery_address: __eliadmin_zod_3().default(false),
      hide_different_delivery_address: __eliadmin_zod_3().default(false),
      region: __eliadmin_zod_4().optional().nullable(),

      address2: __eliadmin_zod_1().optional().nullable(),
      street_number2: __eliadmin_zod_1().optional().nullable(),
      city2: __eliadmin_zod_1().optional().nullable(),
      postcode2: __eliadmin_zod_1().optional().nullable(),
      country2: __eliadmin_zod_0({ id: __eliadmin_zod_4().optional() }).optional().nullable(),

      gdpr: __eliadmin_zod_3()
        .default(false)
        .refine(
          value => value === true,
          VM.TOS_VALIDATION_MESSAGE() + '. ' + VM.GDPR_VALIDATION_MESSAGE(),
        ),
      // must be true
      // conditions: z
      //   .boolean()
      //   .default(false)
      //   .refine(value => value === true),
      newsletter: __eliadmin_zod_3().default(false).optional(),
      create_account: __eliadmin_zod_3().default(false).optional(),

      company_name: __eliadmin_zod_1().optional(),
      iban: __eliadmin_zod_1().optional(),

      password: __eliadmin_zod_1().optional(),
      password_confirmation: __eliadmin_zod_1().optional(),

      note: __eliadmin_zod_1().optional(),

      return_url: __eliadmin_zod_1().optional(),
      newsletter_return_url: __eliadmin_zod_1(),
      check_status_url: __eliadmin_zod_1(),

      is_wholesaler: __eliadmin_zod_3().optional(),
      wholesaler_order_type: __eliadmin_zod_1().default('storage'),

      dropship_name: __eliadmin_zod_1().optional(),
      dropship_surname: __eliadmin_zod_1().optional(),
      dropship_email: __eliadmin_zod_1().optional(),
      dropship_phone: __eliadmin_zod_1().optional(),
      dropship_address: __eliadmin_zod_1().optional(),
      dropship_street_number: __eliadmin_zod_1().optional(),
      dropship_city: __eliadmin_zod_1().optional(),
      dropship_postcode: __eliadmin_zod_1().optional(),
      dropship_country: __eliadmin_zod_0({ id: __eliadmin_zod_1().or(__eliadmin_zod_2()) }).optional(),

      send_heureka_mail: __eliadmin_zod_3().optional(),
    })
    .refine(
      data => {
        const errors: ZodIssue[] = [];
        if (data.wholesaler_order_type === 'dropship') {
          if (!data.dropship_name || data.dropship_name.length === 0) {
            errors.push({
              path: ['dropship_name'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_surname || data.dropship_surname.length === 0) {
            errors.push({
              path: ['dropship_surname'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_email || data.dropship_email.length === 0) {
            errors.push({
              path: ['dropship_email'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_phone || data.dropship_phone.length < 5) {
            errors.push({
              path: ['dropship_phone'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_address || data.dropship_address.length === 0) {
            errors.push({
              path: ['dropship_address'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_street_number || data.dropship_street_number.length === 0) {
            errors.push({
              path: ['dropship_street_number'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_city || data.dropship_city.length === 0) {
            errors.push({
              path: ['dropship_city'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_postcode || data.dropship_postcode.length === 0) {
            errors.push({
              path: ['dropship_postcode'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_country || data.dropship_country.id.toString().length === 0) {
            errors.push({
              path: ['dropship_country.id'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.company_name || data.company_name.length === 0) {
            errors.push({
              path: ['company_name'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
        }

        if (data.different_delivery_address) {
          if (!data.address2 || data.address2.length === 0) {
            errors.push({
              path: ['address2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.street_number2 || data.street_number2.length === 0) {
            errors.push({
              path: ['street_number2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.city2 || data.city2.length === 0) {
            errors.push({
              path: ['city2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.postcode2 || data.postcode2.length === 0) {
            errors.push({
              path: ['postcode2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.country2 || data.country2.id?.toString().length === 0) {
            errors.push({
              path: ['country2.id'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
        }

        if (data.company_buy) {
          if (!data.company?.trim()) {
            errors.push({
              path: ['company'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.ico || data.ico.length === 0) {
            errors.push({
              path: ['ico'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
        }

        if (errors.length > 0) {
          throw new ZodError(errors);
        }

        return true;
      },
      {
        path: ['wholesaler_order_type'],
      },
    );

export type CreateOrderFormValues = z.infer<ReturnType<typeof createOrderSchema>>;

export default function CartPage() {
  const defaultCountry = useGlobalStore(s => s.generalLayout.country);
  // we are batching here to avoid waterfalls suspense is causing
  const [{ data: cart }] = useSuspenseQueries({
    queries: [useCartQueryOpts()],
  });
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const locale = useLocaleStore(s => s.currentLocale);
  const apiKeys = useGlobalStore(s => s.apiKeys);

  const currencySymbol = useGlobalStore.getState().generalLayout.currency;
  const [currencyISO, setCurrencyISO] = useState<string | null>(null);

  const { data: currencies } = useQuery({
    queryKey: [CacheKey.CURRENCIES],
    queryFn: () => fetchCurrencies({ locale }),
  });

  useEffect(() => {
    if (currencies) {
      setCurrencyISO(
        currencies?.data.find(currency => currency.symbol === currencySymbol)?.id || currencySymbol,
      );
    }
  }, [currencies, currencySymbol]);

  const { t } = useTranslations();
  const { currentStep, setStep, currentStepIndex } = useCartStepper();
  const { formValidationMessages } = useFormValidationMessages(t);
  const me = useCustomerStore(s => s.me);

  const form = useForm<CreateOrderFormValues>({
    resolver: zodResolver(createOrderSchema(formValidationMessages)),
    defaultValues: {
      name: me != null ? me.customer.name : '',
      surname: me != null ? me.customer.surname : '',
      email: me != null ? me.customer.email : '',
      phone: me != null ? me.customer.phone : '',
      address: me != null ? me.customer.address : '',
      street_number: me != null ? me.customer.street_number : '',
      postcode: me != null ? me.customer.postcode : '',
      city: me != null ? me.customer.city : '',
      country: { id: me?.customer.country?.id ?? defaultCountry.id },
      address2: me != null ? me.customer.address_delivery : '',
      street_number2: me != null ? me.customer.street_number_delivery : '',
      postcode2: me != null ? me.customer.postcode_delivery : '',
      city2: me != null ? me.customer.city_delivery : '',
      country2: { id: me?.customer.country?.id ?? defaultCountry.id },
      company: me != null ? me.customer.company : '',
      ico: me != null ? me.customer.ico : '',
      dic: me != null ? me.customer.dic : '',
      ic_dph: me != null ? me.customer.ic_dph : '',
      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
      check_status_url: new URL(ROUTES.ORDER_STATUS(), env.NEXT_PUBLIC_APP_URL).toString(),
      dropship_address: '',
      dropship_city: '',
      dropship_country: { id: '' },
      dropship_email: '',
      dropship_name: '',
      dropship_phone: '',
      dropship_postcode: '',
      dropship_street_number: '',
      dropship_surname: '',
      send_heureka_mail: false,
    },
  });

  useCartOrderDraft(form);

  useEffect(() => {
    if (currentStepIndex > 2) {
      form.trigger().then(isValid => {
        if (isValid) {
          return;
        } else {
          setStep(CartStep.ORDER);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex]);

  useEffect(() => {
    // build the id of the active step
    const el = document.getElementById(currentStep);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest', // don’t scroll vertically
        inline: 'center', // center horizontally
      });
    }
  }, [currentStepIndex]);

  if (!cart?.count) {
    return <CartEmpty />;
  }

  const Component = STEP_TO_COMPONENT[currentStep];

  return (
    <StripeProvider publishableKey={apiKeys?.stripe?.public_key ?? ''}>
      <FormProvider {...form}>
        <CartDistanceShipmentSync />
        <DialogProvider>
          <ResponsiveDialogProvider>
            <Component />
            {currencyISO && (
              <TrackComponent
                eventName={'begin_checkout'}
                eventParams={{
                  currency: currencyISO,
                  value: cart.total,
                  send_to: generalLayout.marketing?.gtm_id,
                  items:
                    cart.items?.map((item, index) => ({
                      index,
                      item_id: item.code || item.id.toString(),
                      item_name: item.name,
                      price: item.price,
                      quantity: item.qty,
                    })) || [],
                }}
              />
            )}
          </ResponsiveDialogProvider>
        </DialogProvider>
      </FormProvider>
    </StripeProvider>
  );
}
